import { Store } from '@ngrx/store';
import moment from 'moment';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { closeRtcPeer } from 'app/utils/media/rtc';
import { PeerCandidate } from 'messages/websocket.messages';

import ParticipantRtcStats from '../../preciate-stream/participant-rtc-stats';
import { SubscriptionState } from '../../types/webrtc';
import { StatsCounter } from '../rtc-stats-stream';
import { SubscriptionType } from '../subscription/subscription.types';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { LivestreamSubscriber } from './livestream-subscriber';
import { Subscriber } from './subscriber.types';

abstract class SubscriberCreator extends Loggable {
  public abstract factoryMethod(): Subscriber;
  public subscriber?: Subscriber;
  public subscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  public abstract readonly subscriptionType: SubscriptionType;

  public subscribe(): void {
    if (!this.subscriber) {
      this.subscriber = this.factoryMethod();
    }
    if (
      this.subscriptionState === SubscriptionState.SUBSCRIBED ||
      this.subscriptionState === SubscriptionState.SUBSCRIBING
    ) {
      return;
    }

    const { subscriberId, subscriberType, videoRoomService } = this.subscriber;

    const config = videoRoomService.createRtcConfig();
    const peer = new RTCPeerConnection(config);

    this.subscriber.peerTime = Date.now();

    peer.onconnectionstatechange = () => {
      if (!this.subscriber) {
        return;
      }
      const { connectionState } = peer;
      switch (connectionState) {
        case 'connected':
          this.subscriptionState = SubscriptionState.SUBSCRIBED;
          this.subscriber.peerConnectionTime = Date.now();
          this.sendRtcStats(connectionState).catch((e) => this.error('Error sending RTC stats', e));
          this.startRtcStats();
          break;
        case 'failed':
          this.subscriptionState = SubscriptionState.FAILED;
          this.handleConnectionFailed();
          break;
        default:
          break;
      }
    };

    peer.oniceconnectionstatechange = (): void => this.subscriber?.onIceConnectionStateChange(peer);
    peer.onicegatheringstatechange = (): void => this.subscriber?.onIceGatheringStateChange(peer);
    peer.onicecandidateerror = (e): void => this.subscriber?.onIceCandidateError(e);
    peer.onicecandidate = (event): void => this.sendCandidate(event, this.subscriber?.peerTime);
    peer.ontrack = this.onTrack.bind(this);

    this.subscriber.peer = peer;
    this.info(`Subscribe - ${subscriberType}`, { subscriberId, peerTime: this.subscriber.peerTime });
    this.subscriber.sendSubscribeMessage();
  }

  private sendCandidate(event: RTCPeerConnectionIceEvent, peerTime: number | undefined): void {
    if (!this.subscriber) {
      return;
    }
    const { candidate } = event;
    const { subscriberId, subscriberType } = this.subscriber;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    if (!peerTime) {
      this.info(`Peer time is undefined, done - ${subscriberType} - ${subscriberId}`);
      return;
    }

    if (!candidate.sdpMid) {
      this.error('Candidate sdpMid is unexpectedly null', candidate);
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    this.subscriber.sendCandidateMessage(candidate as PeerCandidate, peerTime);
  }

  private onTrack(event: RTCTrackEvent): void {
    if (!this.subscriber) {
      return;
    }
    const { subscriberId, subscriberType } = this.subscriber;
    if (event.track.kind === 'video') {
      this.info(`Video track received for ${subscriberType} ${subscriberId}`);
      this.subscriber.videoStream = event.streams[0];
    } else if (event.track.kind === 'audio') {
      this.info(`Audio track received for ${subscriberType} ${subscriberId}`);
      this.subscriber.audioStream = event.streams[0];
      this.subscriber.audioElement.srcObject = this.subscriber.audioStream;
    }
    this.subscriber.onMediaStreamChange();
  }

  private async sendRtcStats(state: string): Promise<void> {
    if (!this.subscriber) {
      return;
    }
    const stats = await this.subscriber.peer?.getStats();
    if (stats) {
      const rtcReports: RTCStatsReport[] = [];
      stats.forEach((report) => rtcReports.push(report));
      this.info(`RTC Stats - ${this.subscriber.subscriberType}`, {
        subscriberId: this.subscriber.subscriberId,
        rtcReports,
        state,
      });
    } else {
      this.info(`No RTC stats - ${this.subscriber.subscriberType}`, {
        subscriberId: this.subscriber.subscriberId,
        state,
      });
    }
  }

  /**
   * RTC Stats
   */
  private startRtcStats(): void {
    if (!this.subscriber) {
      return;
    }
    const { audioEnabled, peer, subscriberId, subscriberType, videoEnabled } = this.subscriber;
    this.info(`RTC Stats START for ${this.subscriber.subscriberType}:`, subscriberId);
    const rtcStats = (this.subscriber.rtcStats = new ParticipantRtcStats(subscriberId));
    if (this.subscriber.rtcVideoStatsInterval) {
      clearInterval(this.subscriber.rtcVideoStatsInterval);
    }
    this.subscriber.rtcVideoStatsInterval = setInterval(async () => {
      // Validate if peer and videoTrack is still available
      let isVideoStuck = false;
      let isAudioStuck = false;

      if (!peer) {
        return;
      }

      const stats = await peer.getStats();
      if (videoEnabled) {
        isVideoStuck = rtcStats.isVideoStuck(stats, subscriberId, subscriberType);
      }

      if (audioEnabled) {
        isAudioStuck = rtcStats.isAudioStuck(stats, subscriberId, subscriberType);
      }

      if (isVideoStuck || isAudioStuck) {
        this.warn(`Media is Stuck RESUBSCRIBE - ${subscriberType}`, {
          subscriberId,
          isVideoStuck,
          isAudioStuck,
        });
        this.subscriber?.destroyRtcStats();
        this.handleConnectionFailed();
      }
    }, 1000);
  }

  private handleConnectionFailed(): void {
    if (!this.subscriber) {
      return;
    }

    switch (this.subscriptionType) {
      case SubscriptionType.MIRROR:
        this.subscriber.videoRoomService.rtcStatsStreamService.incrementCounter(
          StatsCounter.MIRROR_SUBSCRIBER_CONNECTION_FAILED,
        );
        break;
      case SubscriptionType.MIX:
        this.subscriber.videoRoomService.rtcStatsStreamService.incrementCounter(
          StatsCounter.MIX_SUBSCRIBER_CONNECTION_FAILED,
        );
        break;
      case SubscriptionType.LIVE_STREAM:
        this.subscriber.videoRoomService.rtcStatsStreamService.incrementCounter(
          StatsCounter.LIVE_STREAM_SUBSCRIBER_CONNECTION_FAILED,
        );
        break;
      default:
        throw new Error(`Unexpected subscription type: ${this.subscriptionType}`);
    }

    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.subscriber.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.subscriber.videoRoomService.advanceReconnectionState({ connectionTime });
      // Clear the current connection time to avoid advancing state twice
      this.subscriber.peerConnectionTime = undefined;
    }

    this.sendRtcStats('failed').catch((e) => this.error('Error sending RTC stats', e));
    this.retrySubscription();
  }

  private retrySubscription(): void {
    if (!this.subscriber) {
      return;
    }
    this.subscriptionState = SubscriptionState.RESUBSCRIBE;
    this.subscriber.retryTimeout = setTimeout(() => {
      if (!this.subscriber) {
        return;
      }
      this.subscriber.retryTimeout = undefined;
      this.unsubscribe();
      this.subscribe();
    }, 1500);
  }

  public unsubscribe(): void {
    if (!this.subscriber) {
      return;
    }
    const { peerTime, subscriberId, subscriberType } = this.subscriber;
    if (this.subscriber.peer) {
      this.subscriptionState = SubscriptionState.UNSUBSCRIBING;
      this.info(`Unsubscribe - ${subscriberType}`, { subscriberId, peerTime });
      closeRtcPeer(this.subscriber.peer);
      this.subscriber.peer = undefined;
      this.subscriber.peerTime = undefined;

      this.subscriber.sendUnSubscribeMessage();

      // Remove RTC Stats Interval
      this.subscriber.destroyRtcStats();
    }
  }
}

export class LivestreamSubscriberCreator extends SubscriberCreator {
  public subscriptionType = SubscriptionType.LIVE_STREAM;
  public constructor(
    private videoRoomService: VideoRoomService,
    private websocketService: WebsocketService,
    private store: Store<AppState>,
    private subscriberId: string,
    private subscriberPeerTime: number,
    private audioEnabled: boolean,
    private videoEnabled: boolean,
    private onMediaStreamChange: (subscriberId: string, audio?: MediaStream, video?: MediaStream) => void,
  ) {
    super();
    this.setDisplayName('LivestreamSubscriberCreator');
  }
  public factoryMethod(): LivestreamSubscriber {
    return new LivestreamSubscriber(
      this.videoRoomService,
      this.subscriberId,
      this.audioEnabled,
      this.videoEnabled,
      this.websocketService,
      this.store,
      this.subscriberPeerTime,
      this.onMediaStreamChange,
    );
  }
}
